import { useCallback, useMemo } from 'react';

import { useToasts } from '@air/provider-toast';

import { type OnShortUrlUpdate } from '~/components/ShareModal/types/onShortUrlUpdate';
import {
  type AssetShareLinkOption,
  type AssetShareLinkSelectionContext,
  applyShowVersionsConversionToUpdateProps,
  buildEmptySlotShareLinkDisplay,
  getAssetShareLinkOptionSelection,
  isAssetShareLinkEnabled,
  resolveShareLinkObjectType,
} from '~/components/SharePrivateClipModal/buildAssetShareLinkSlots';
import { useCreateShareLinkForSlot } from '~/components/SharePrivateClipModal/hooks/useCreateShareLinkForSlot';
import { useUpdateAssetScopedShareLink } from '~/components/SharePrivateClipModal/hooks/useUpdateAssetScopedShareLink';
import { type AssetScopedShareUrlResponseWithSource } from '~/components/SharePrivateClipModal/types';
import { type PrivateAssetOpenLocation } from '~/hooks/usePrivateAssetOpenLocation';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useGetDefaultSettings } from '~/swr-hooks/shortUrl/useGetDefaultSettings';
import { type AssetScopedShareLinkListItem } from '~/swr-hooks/shortUrl/useListAssetShareLinks';
import { shareUrlUpdatedLinkAccess } from '~/utils/shortUrl/shareUrlUpdatedAnalytics';

type UseAssetScopedShareLinkEditorParams = {
  assetId: string;
  assetShareLinksItems: AssetScopedShareLinkListItem[];
  analyticsLocation: PrivateAssetOpenLocation | null;
  canCreateShareLink: boolean;
  canViewAssetVersions: boolean;
  selectedOption: AssetShareLinkOption | undefined;
  selectionContext: AssetShareLinkSelectionContext;
};

export const useAssetScopedShareLinkEditor = ({
  assetId,
  assetShareLinksItems,
  analyticsLocation,
  canCreateShareLink,
  canViewAssetVersions,
  selectedOption,
  selectionContext,
}: UseAssetScopedShareLinkEditorParams) => {
  const { showToast } = useToasts();
  const { currentWorkspace } = useCurrentWorkspace();

  const { selectedOptionKey, selectedSlotId, selectedSlotShortUrl } = getAssetShareLinkOptionSelection(selectedOption);
  const hasActiveShareLink = !!selectedSlotShortUrl?.id;
  const isShareLinkPublic = isAssetShareLinkEnabled(selectedSlotShortUrl);
  const defaultSettingsObjectType = resolveShareLinkObjectType({ slotId: selectedSlotId });

  const { data: defaultSettings, isLoading: isLoadingDefaultSettings } = useGetDefaultSettings(
    {
      objectType: defaultSettingsObjectType,
      workspaceId: currentWorkspace?.id ?? '',
    },
    { enabled: !hasActiveShareLink && !!currentWorkspace?.id },
  );

  const shortIdInfo = hasActiveShareLink ? selectedSlotShortUrl : undefined;

  const emptySlotShareLinkDisplay = useMemo(
    () => (defaultSettings ? buildEmptySlotShareLinkDisplay(defaultSettings) : null),
    [defaultSettings],
  );
  const displayShortIdInfo = shortIdInfo ?? emptySlotShareLinkDisplay;
  const areSettingsDisabled = !hasActiveShareLink || !isShareLinkPublic;

  const { mutateAsync: updateShareLink, isPending: isUpdatingShareLink } = useUpdateAssetScopedShareLink({
    assetId,
  });
  const { createShareLinkForSlot, isCreatingShareLink, isLoadingVersionClips } = useCreateShareLinkForSlot({
    assetId,
    assetShareLinksItems,
    analyticsLocation,
    canViewAssetVersions,
    defaultSettings,
    selectedSlotId,
    selectionContext,
  });
  const isEnableToggleLoading =
    isUpdatingShareLink ||
    isCreatingShareLink ||
    isLoadingVersionClips ||
    (!hasActiveShareLink && isLoadingDefaultSettings);
  const isSettingsLoading = isUpdatingShareLink || (!hasActiveShareLink && isLoadingDefaultSettings);

  const onShortUrlUpdate = useCallback<OnShortUrlUpdate<AssetScopedShareUrlResponseWithSource>>(
    ({ props, analytics }) => {
      if (!shortIdInfo || !selectedSlotShortUrl) {
        return Promise.reject(new Error('No share link for selected slot'));
      }

      return updateShareLink({
        shortId: shortIdInfo.id,
        patch: applyShowVersionsConversionToUpdateProps(shortIdInfo, props),
        analytics,
      });
    },
    [selectedSlotShortUrl, shortIdInfo, updateShareLink],
  );

  const handleToggleEnablePublicLink = useCallback(
    async (enabled: boolean) => {
      if (enabled && !hasActiveShareLink) {
        if (!canCreateShareLink) {
          return;
        }

        try {
          await createShareLinkForSlot();
        } catch {
          showToast('Something went wrong. Please try again later.', {
            color: 'red',
          });
        }

        return;
      }

      if (!hasActiveShareLink || !shortIdInfo) {
        return;
      }

      try {
        await onShortUrlUpdate({ props: { disabled: !enabled }, analytics: shareUrlUpdatedLinkAccess(enabled) });
      } catch {
        showToast('Something went wrong. Please try again later.', {
          color: 'red',
        });
      }
    },
    [canCreateShareLink, createShareLinkForSlot, hasActiveShareLink, onShortUrlUpdate, shortIdInfo, showToast],
  );

  return {
    areSettingsDisabled,
    displayShortIdInfo,
    handleToggleEnablePublicLink,
    hasActiveShareLink,
    isEnableToggleLoading,
    isSettingsLoading,
    isShareLinkPublic,
    onShortUrlUpdate,
    selectedOptionKey,
    shortIdInfo,
  };
};
